import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { WorkloadGroup } from '../today-clinic-model'
import styles from '../OverviewPage.module.css'

export type WorkloadHourPhase = 'elapsed' | 'current' | 'upcoming'

const phaseClass: Record<WorkloadHourPhase, string | undefined> = {
  elapsed: styles.workloadElapsed,
  current: styles.workloadCurrent,
  upcoming: undefined,
}

export function WorkloadHour({
  bucket,
  maximum,
  timeZone,
  phase = 'upcoming',
}: {
  bucket: WorkloadGroup
  maximum: number
  timeZone: string
  phase?: WorkloadHourPhase
}) {
  const id = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const [position, setPosition] = useState<{
    left: number
    top: number
    maxHeight: number
    transform?: string
  } | null>(null)
  const cancelDismiss = () => clearTimeout(timer.current)
  const close = () => {
    cancelDismiss()
    setPosition(null)
  }
  const open = () => {
    cancelDismiss()
    const rect = trigger.current?.getBoundingClientRect()
    if (!rect) return
    const width = Math.min(320, window.innerWidth - 24)
    const height = Math.min(340, window.innerHeight - 24)
    const below = window.innerHeight - rect.bottom - 16
    const above = rect.top - 16
    const useBelow = below >= height || below >= above
    const maxHeight = Math.max(100, Math.min(height, useBelow ? below : above))
    setPosition({
      left: Math.max(
        12,
        Math.min(
          rect.left + rect.width / 2 - width / 2,
          window.innerWidth - width - 12
        )
      ),
      top: useBelow ? rect.bottom + 6 : rect.top - 6,
      transform: useBelow ? undefined : 'translateY(-100%)',
      maxHeight,
    })
  }
  const dismissSoon = () => {
    cancelDismiss()
    timer.current = setTimeout(() => setPosition(null), 180)
  }

  useEffect(() => () => clearTimeout(timer.current), [])
  useEffect(() => {
    if (!position) return
    const dismiss = () => setPosition(null)
    const outside = (event: Event) => {
      const target = event.target as Node
      if (
        !panel.current?.contains(target) &&
        !trigger.current?.contains(target)
      )
        dismiss()
    }
    const scroll = (event: Event) => {
      if (!panel.current?.contains(event.target as Node)) dismiss()
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (panel.current?.contains(document.activeElement))
        trigger.current?.focus()
      dismiss()
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    window.addEventListener('scroll', scroll, true)
    window.addEventListener('resize', dismiss)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('scroll', scroll, true)
      window.removeEventListener('resize', dismiss)
    }
  }, [position])

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`${styles.workloadColumn} ${phaseClass[phase] ?? ''} ${bucket.count === 0 ? styles.workloadEmpty : ''}`}
        aria-label={`${bucket.label}: ${bucket.count} appointment${bucket.count === 1 ? '' : 's'} starting`}
        aria-expanded={Boolean(position)}
        aria-controls={position ? id : undefined}
        onMouseEnter={open}
        onMouseLeave={dismissSoon}
        onFocus={open}
        onBlur={(event) => {
          if (!panel.current?.contains(event.relatedTarget)) close()
        }}
        onClick={open}
        onKeyDown={(event) => {
          if (
            position &&
            (event.key === 'ArrowDown' ||
              (event.key === 'Tab' && !event.shiftKey))
          ) {
            event.preventDefault()
            panel.current?.focus()
          }
        }}
      >
        <span className={styles.workloadCount}>{bucket.count}</span>
        <span className={styles.workloadTrack}>
          <span
            className={styles.workloadBar}
            style={{ height: `${(bucket.count / maximum) * 100}%` }}
          />
        </span>
        <span className={styles.workloadLabel}>{bucket.label}</span>
      </button>
      {position &&
        createPortal(
          <div
            ref={panel}
            id={id}
            role="region"
            aria-label={`${bucket.label} appointments`}
            tabIndex={0}
            className={styles.workloadTooltip}
            style={position}
            onMouseEnter={cancelDismiss}
            onMouseLeave={dismissSoon}
            onFocus={cancelDismiss}
            onBlur={close}
            onKeyDown={(event) => {
              if (event.key === 'Tab') {
                trigger.current?.focus()
                close()
              }
            }}
          >
            <header>
              <strong>{bucket.label}</strong>
              <span>
                {bucket.count} appointment{bucket.count === 1 ? '' : 's'}
              </span>
              <small>{timeZone}</small>
            </header>
            {bucket.count === 0 ? (
              <p>No appointments in this hour</p>
            ) : (
              <ul>
                {bucket.appointments.map((appointment) => (
                  <li key={appointment.appointmentId}>
                    <div>
                      <strong>{appointment.patientDisplayName}</strong>
                      <span>{appointment.providerName ?? 'Unassigned'}</span>
                    </div>
                    <time
                      dateTime={new Date(
                        appointment.scheduledAtMs
                      ).toISOString()}
                    >
                      {new Intl.DateTimeFormat(undefined, {
                        timeZone,
                        hour: 'numeric',
                        minute: '2-digit',
                      }).format(appointment.scheduledAtMs)}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body
        )}
    </>
  )
}
